import type { Dispatch, SetStateAction } from 'react';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { Pagination } from '~/components/Pagination';
import { SCROLL_COLLAPSE_EASING_DURATION_CLASS } from '~/components/ScrollCollapse/constants/classes';
import { useScrollCollapseContext } from '~/components/ScrollCollapse/hooks/useScrollCollapseContext';
import { cn } from '~/lib/cn';
import { ArtistSearchBar } from '~/modules/artist-roster/components/ArtistSearchBar/ArtistSearchBar';
import { FilterButton } from '~/modules/artist-roster/components/FilterButton/FilterButton';
import { DEFAULT_ROSTER_PAGE_SIZE } from '~/modules/artist-roster/constants';
import type { ArtistUrlParamsHook } from '~/modules/artist-roster/hooks/useUrlParams/types';
import { getRosterStatus } from '~/modules/artist-roster/lib/roster';
import type { ArtistAppliedLabelFilter } from '~/modules/artist-roster/pages/roster/components/ArtistFilterPanel/ArtistAppliedFilter/ArtistAppliedFilterList';
import type { RosterFilters } from '~/modules/artist-roster/pages/roster/components/ArtistFilterPanel/types';
import { useArtistQuery } from '~/modules/artist-roster/pages/roster/hooks/useArtistQuery';
import type { RosterFilterUrlParams } from '~/modules/artist-roster/pages/roster/hooks/useRosterUrlParams/types';
import type { FiltersMetadata, LabelData } from '~/modules/artist-roster/types';
import { useAnalytics } from '~/modules/monitoring/analytics/useAnalytics';

import { ArtistExport } from './ArtistExport/ArtistExport';
import { ArtistAppliedFilterList } from './ArtistFilterPanel/ArtistAppliedFilter/ArtistAppliedFilterList';
import { DEFAULT_ARTIST_FILTERS } from './ArtistFilterPanel/constants';

interface RosterHeaderProps {
  rosterUrlParamsProps: ArtistUrlParamsHook<RosterFilterUrlParams>;
  filteredLabels: LabelData[];
  setFilteredLabels: (labels: LabelData[]) => void;
  setIsFilterPanelOpen: Dispatch<SetStateAction<boolean>>;
  handleApplyFilters: (appliedFilters: RosterFilters) => void;
  allLabels?: LabelData[];
}

export function RosterHeader({
  rosterUrlParamsProps,
  filteredLabels,
  setFilteredLabels,
  setIsFilterPanelOpen,
  handleApplyFilters,
  allLabels,
}: RosterHeaderProps) {
  const { t } = useTranslation('roster');
  const { trackEvent } = useAnalytics('Artist Roster');
  const { filters, pageNumber, updateFilters, setPageNumber } =
    rosterUrlParamsProps;
  const { labelIds, searchText, isCrossover, isOnRoster } = filters;
  const { headerRef, getInterpolatedValue } = useScrollCollapseContext();

  // This is the list of applied filter objects displayed under the search bar.
  const appliedFilterItems = useMemo<ArtistAppliedLabelFilter[]>(() => {
    return filteredLabels.map((label) => {
      const { labelId, name, updatedAt, updatedBy } = label;

      return {
        labelId,
        displayName: name,
        updatedAt,
        updatedBy,
      };
    });
  }, [filteredLabels]);

  const filtersMetadata: FiltersMetadata = useMemo(() => {
    return {
      areFiltersApplied:
        filteredLabels.length > 0 || isCrossover || isOnRoster !== undefined,
      filterCount:
        filteredLabels.length +
        (isCrossover ? 1 : 0) +
        (isOnRoster !== undefined ? 1 : 0),
    };
  }, [filteredLabels, isCrossover, isOnRoster]);

  const { data: artistsData, isLoading: isLoadingArtists } = useArtistQuery({
    searchText,
    labelIds,
    isCrossover,
    isOnRoster,
    pageNumber,
  });

  const totalArtists = artistsData?.pagination.total ?? 0;

  const handleSearchInputClear = useCallback(() => {
    // NOTE: Use undefined here to specifically clear the search text from the URL.
    updateFilters({ searchText: undefined });
  }, [updateFilters]);

  const handleSubmitSearch = useCallback(
    (searchValue: string) => {
      trackEvent('rosterArtistSearch', {
        roster_label_filters_count: labelIds.length,
        roster_label_filters_applied: labelIds.length > 0,
        roster_crossover_filter_applied: isCrossover,
        roster_status: getRosterStatus(isOnRoster),
        search_term: searchValue,
      });

      // Make sure to clear the search if it's an empty string. We clear by
      // passing undefined to the updateFilters function.
      updateFilters({ searchText: searchValue || undefined });
    },
    [isCrossover, labelIds.length, isOnRoster, trackEvent, updateFilters],
  );

  const handleRemoveLabelFilter = useCallback(
    (deletedLabelId: number) => {
      const newFilteredLabelData = filteredLabels.filter(
        ({ labelId }) => labelId !== deletedLabelId,
      );

      setFilteredLabels(newFilteredLabelData);

      updateFilters({
        labelIds: newFilteredLabelData.map((label) => label.labelId),
        isCrossover,
      });
    },
    [filteredLabels, isCrossover, updateFilters, setFilteredLabels],
  );

  const handleRemoveCrossoverFilter = useCallback(() => {
    updateFilters({
      isCrossover: false,
    });
  }, [updateFilters]);

  const handleRemoveOnRosterFilter = useCallback(() => {
    updateFilters({
      isOnRoster: undefined,
    });
  }, [updateFilters]);

  const trackPaginationEvent = useCallback(
    (directionToNextPage: boolean) => {
      trackEvent('rosterTablePaginationClick', {
        roster_label_filters_count: labelIds.length,
        roster_crossover_filter_applied: isCrossover,
        roster_label_filters_applied: labelIds.length > 0,
        roster_status: getRosterStatus(isOnRoster),
        direction: directionToNextPage ? 'next_page' : 'previous_page',
      });
    },
    [isCrossover, labelIds.length, isOnRoster, trackEvent],
  );

  const setPageNumberWithTracking = useCallback(
    (pageNum: number) => {
      trackPaginationEvent(pageNum > pageNumber);
      setPageNumber(pageNum);
    },
    [pageNumber, setPageNumber, trackPaginationEvent],
  );

  const handleClearFilters = useCallback(() => {
    handleApplyFilters(DEFAULT_ARTIST_FILTERS);
  }, [handleApplyFilters]);

  const handleShowFilters = useCallback(
    (isOpen: boolean) => {
      setIsFilterPanelOpen(isOpen);

      if (isOpen) {
        trackEvent('rosterFiltersClick', {
          roster_label_filters_count: labelIds.length,
          roster_crossover_filter_applied: isCrossover,
          roster_label_filters_applied: labelIds.length > 0,
          roster_status: getRosterStatus(isOnRoster),
        });
      }
    },
    [
      isCrossover,
      labelIds.length,
      isOnRoster,
      trackEvent,
      setIsFilterPanelOpen,
    ],
  );

  const handleAppliedFiltersMoreClick = useCallback(() => {
    setIsFilterPanelOpen((isOpen) => !isOpen);
  }, [setIsFilterPanelOpen]);

  return (
    <div
      className="sticky top-0 z-20 bg-gradient bg-[length:100%,auto] bg-fixed bg-no-repeat px-xl pt-lg bg-level-01/full"
      ref={headerRef}
    >
      <div className="mb-lg flex items-end justify-between">
        <h1
          className={cn(
            'text-display-md font-bold will-change-[font-size]',
            SCROLL_COLLAPSE_EASING_DURATION_CLASS,
          )}
          // Transition from "text-display-md" to "text-headline-sm".
          style={{ fontSize: getInterpolatedValue(32, 18) }}
        >
          {t('header.heading')}
        </h1>
        {!isLoadingArtists && !!totalArtists ? (
          <ArtistExport
            search={searchText}
            isCrossover={isCrossover}
            isOnRoster={isOnRoster}
            filteredLabels={labelIds}
            allLabels={allLabels}
          />
        ) : null}
      </div>
      <div className="mb-lg flex gap-md" role="search">
        <ArtistSearchBar
          initialSearchText={searchText ?? ''}
          placeholder={t('filters.searchForArtist')}
          onSubmit={handleSubmitSearch}
          onClear={handleSearchInputClear}
        />
        <FilterButton
          filtersMetadata={filtersMetadata}
          onShowFilters={handleShowFilters}
        />
      </div>
      {isCrossover || isOnRoster !== undefined || labelIds.length > 0 ? (
        <ArtistAppliedFilterList
          isCrossover={isCrossover}
          isOnRoster={isOnRoster}
          labelFilters={appliedFilterItems}
          onRemoveCrossover={handleRemoveCrossoverFilter}
          onRemoveOnRoster={handleRemoveOnRosterFilter}
          onRemoveLabel={handleRemoveLabelFilter}
          onClear={handleClearFilters}
          onMoreClick={handleAppliedFiltersMoreClick}
        />
      ) : null}
      <div className="mb-md inline-flex min-h-xl w-full items-center justify-between">
        <div className="flex gap-xs">
          <h2>{t('header.subheading')}</h2>
          {!isLoadingArtists ? (
            <div className="text-subtle">({totalArtists.toLocaleString()})</div>
          ) : null}
        </div>
        {totalArtists > DEFAULT_ROSTER_PAGE_SIZE ? (
          <Pagination
            page={pageNumber}
            total={totalArtists}
            perPage={DEFAULT_ROSTER_PAGE_SIZE}
            setPageNumber={setPageNumberWithTracking}
            showAsLink={true}
          />
        ) : null}
      </div>
    </div>
  );
}

RosterHeader.displayName = 'RosterHeader';
